import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { PHONE_TEL, PHONE_DISPLAY, EMAIL_SALES } from "../../contact";
import csChat from "../../assets/data/translation/cs/chatbot.json";
import enChat from "../../assets/data/translation/en/chatbot.json";
import "./ChatBot.css";

const MAX_STEPS = 3;
const TYPING_DELAY = 950;

const IconPhone = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z"/>
  </svg>
);
const IconEmail = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
  </svg>
);
const IconReset = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.65 6.35A7.96 7.96 0 0 0 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0 1 12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
  </svg>
);
const IconClose = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
  </svg>
);
const IconChat = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="white" aria-hidden="true">
    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
  </svg>
);

function playPing() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.08);
    gain.gain.setValueAtTime(0.07, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.4);
  } catch (e) {}
}

function getInitialMessages(data) {
  return [
    { type: "bot", text: data.welcome },
    { type: "bot", text: data.tree["root"].question },
  ];
}

function track(name, params = {}) {
  if (window.gtag) window.gtag("event", name, params);
}

function ChatBot() {
  const { i18n } = useTranslation();
  const data = i18n.language === "en" ? enChat : csChat;

  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState(["root"]);
  const [messages, setMessages] = useState(() => getInitialMessages(data));
  const [isTyping, setIsTyping] = useState(false);

  const autoOpenDone = useRef(false);
  const prevLang = useRef(i18n.language);
  const bottomRef = useRef(null);
  const typingTimer = useRef(null);

  const currentKey = history[history.length - 1];
  const currentNode = data.tree[currentKey];
  const isCTA = !currentNode?.options;
  const step = Math.min(history.length - 1, MAX_STEPS);

  useEffect(() => {
    if (autoOpenDone.current) return;
    autoOpenDone.current = true;
    const timer = setTimeout(() => {
      setOpen(true);
      track("chat_auto_open");
    }, 30000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  useEffect(() => {
    if (i18n.language === prevLang.current) return;
    prevLang.current = i18n.language;
    const d = i18n.language === "en" ? enChat : csChat;
    clearTimeout(typingTimer.current);
    setIsTyping(false);
    setHistory(["root"]);
    setMessages(getInitialMessages(d));
  }, [i18n.language]);

  useEffect(() => () => clearTimeout(typingTimer.current), []);

  const handleOpen = () => {
    setOpen(true);
    playPing();
    track("chat_open", { lang: i18n.language });
  };

  const handleClose = () => setOpen(false);

  const handleReset = () => {
    clearTimeout(typingTimer.current);
    setIsTyping(false);
    setHistory(["root"]);
    setMessages(getInitialMessages(data));
    track("chat_reset", { lang: i18n.language });
  };

  const handleOption = (opt) => {
    const nextKey = opt.next;
    const nextNode = data.tree[nextKey];
    const nextText = nextNode?.options ? nextNode.question : data.ctaText;

    setMessages((prev) => [...prev, { type: "user", text: opt.label }]);
    setHistory((prev) => [...prev, nextKey]);
    setIsTyping(true);
    track("chat_step", { step: nextKey, lang: i18n.language });

    typingTimer.current = setTimeout(() => {
      setIsTyping(false);
      setMessages((prev) => [...prev, { type: "bot", text: nextText }]);
      playPing();
    }, TYPING_DELAY);
  };

  const handleBack = () => {
    if (history.length <= 1 || isTyping) return;
    setHistory((prev) => prev.slice(0, -1));
    setMessages((prev) => prev.slice(0, -2));
  };

  const handleCTA = (type) => {
    track("chat_cta_click", { type, step: currentKey, lang: i18n.language });
  };

  const subject = isCTA ? (currentNode?.subject ?? "") : "";
  const mailtoHref = `mailto:${EMAIL_SALES}?subject=${encodeURIComponent(subject)}`;

  return (
    <>
      <button
        className={`chatbot-bubble${open ? " chatbot-bubble--hidden" : ""}`}
        onClick={handleOpen}
        aria-label={data.openLabel}
      >
        <IconChat />
      </button>

      <div
        className={`chatbot-window${open ? " chatbot-window--open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="MARC Steel"
      >
        <div className="chatbot-header">
          <div className="chatbot-header-left">
            <div className="chatbot-header-avatar" aria-hidden="true">M</div>
            <div className="chatbot-header-info">
              <span className="chatbot-header-title">MARC Steel</span>
              <span className="chatbot-header-status">online</span>
            </div>
          </div>
          <div className="chatbot-header-actions">
            <button
              className="chatbot-icon-btn"
              onClick={handleReset}
              title={data.reset}
              aria-label={data.reset}
            >
              <IconReset />
            </button>
            <button
              className="chatbot-icon-btn"
              onClick={handleClose}
              aria-label={i18n.language === "en" ? "Close" : "Zavřít"}
            >
              <IconClose />
            </button>
          </div>
        </div>

        <div className="chatbot-messages">
          {messages.map((msg, i) => (
            <div key={i} className={`chatbot-msg chatbot-msg--${msg.type}`}>
              {msg.type === "bot" && (
                <div className="chatbot-avatar" aria-hidden="true">M</div>
              )}
              <div className="chatbot-msg-bubble">{msg.text}</div>
            </div>
          ))}

          {isTyping && (
            <div className="chatbot-typing">
              <div className="chatbot-avatar" aria-hidden="true">M</div>
              <div className="chatbot-typing-dots" aria-label="píše...">
                <div className="chatbot-typing-dot" />
                <div className="chatbot-typing-dot" />
                <div className="chatbot-typing-dot" />
              </div>
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        <div className="chatbot-footer">
          <div className="chatbot-progress" aria-hidden="true">
            {[1, 2, 3].map((s) => {
              const isDone = isCTA || step >= s;
              const isActive = !isCTA && step === s - 1;
              return (
                <div
                  key={s}
                  className={`chatbot-progress-dot${isDone ? " chatbot-progress-dot--done" : isActive ? " chatbot-progress-dot--active" : ""}`}
                />
              );
            })}
          </div>

          {!isCTA ? (
            <div className="chatbot-options">
              {!isTyping && currentNode?.options?.map((opt, i) => (
                <button
                  key={i}
                  className="chatbot-option"
                  onClick={() => handleOption(opt)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          ) : (
            <div className="chatbot-cta">
              <a
                href={`tel:${PHONE_TEL}`}
                className="chatbot-cta-btn chatbot-cta-call"
                onClick={() => handleCTA("call")}
              >
                <IconPhone /> {data.callLabel}&nbsp;&nbsp;{PHONE_DISPLAY}
              </a>
              <a
                href={mailtoHref}
                className="chatbot-cta-btn chatbot-cta-email"
                onClick={() => handleCTA("email")}
              >
                <IconEmail /> {data.emailLabel}
              </a>
            </div>
          )}

          {history.length > 1 && !isTyping && (
            <button className="chatbot-back" onClick={handleBack}>
              {data.back}
            </button>
          )}
        </div>
      </div>
    </>
  );
}

export default ChatBot;
